import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE,
  abs,
  canonical as toCanonical,
  orgSchema,
  websiteSchema,
  webPageSchema,
  imageDimensions,
} from "../lib/seo";

/**
 * Reusable SEO component. Renders title, meta description, canonical,
 * robots, Open Graph, Twitter/X and optional JSON-LD structured data.
 *
 * Props:
 *  - title, description, path, image, type ('website' | 'article')
 *  - noindex (boolean)
 *  - schema (object | array of JSON-LD objects)
 */
const SEO = ({
  title,
  description,
  path = "/",
  image,
  type = "website",
  noindex = false,
  schema,
}) => {
  const fullTitle = title || SITE.defaultTitle;
  const desc = description || SITE.defaultDescription;
  const url = toCanonical(path);
  const imagePath = image || SITE.defaultImage;
  const img = abs(imagePath);
  const { width: imgWidth, height: imgHeight } = imageDimensions(imagePath);
  const schemas = Array.isArray(schema) ? schema : schema ? [schema] : [];
  const hasBreadcrumb = schemas.some((s) => s["@type"] === "BreadcrumbList");
  // One connected @graph per page; shared entities referenced by @id.
  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      orgSchema(),
      websiteSchema(),
      ...(noindex
        ? []
        : [webPageSchema({ path, title: fullTitle, description: desc, image, breadcrumb: hasBreadcrumb })]),
      ...schemas,
    ],
  };

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {!noindex && <link rel="canonical" href={url} />}
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content={String(imgWidth)} />
      <meta property="og:image:height" content={String(imgHeight)} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      <script type="application/ld+json">{JSON.stringify(graph)}</script>
    </Helmet>
  );
};

export default SEO;
